import { Margin, usePDF } from "react-to-pdf";
import React, { useCallback, useMemo } from "react";
import { flushSync } from "react-dom";

const pageSettings = {
  margin: Margin.SMALL,
  format: "letter",
};

// Letter size and the mm-to-px factor react-to-pdf uses to slice the screenshot into pages.
const PAGE_SIZE_MM = { width: 215.9, height: 279.4 };
const MM_TO_PX = 3.77952755906;
// Anything this short is kept whole (a field with its label, value and score box); taller blocks are split by their children.
const MAX_ROW_HEIGHT = 150;

const getPageHeight = (target: HTMLElement) => {
  const availableWidth = (PAGE_SIZE_MM.width - 2 * pageSettings.margin) * MM_TO_PX;
  const availableHeight = (PAGE_SIZE_MM.height - 2 * pageSettings.margin) * MM_TO_PX;
  return availableHeight * Math.max(1, target.getBoundingClientRect().width / availableWidth);
};

const isGrid = (element: Element) => getComputedStyle(element).display.includes("grid");

const pushToNextPage = (row: Element, offset: number) => {
  const parent = row.parentElement!;
  let first = row;
  // In a grid the whole row has to move, so the spacer goes before its first cell.
  if (isGrid(parent)) {
    const { top } = row.getBoundingClientRect();
    while (
      first.previousElementSibling &&
      Math.abs(first.previousElementSibling.getBoundingClientRect().top - top) < 1
    ) {
      first = first.previousElementSibling;
    }
  }
  const spacer = document.createElement("div");
  spacer.style.height = `${offset}px`;
  spacer.style.flexShrink = "0";
  spacer.style.gridColumn = "1 / -1";
  parent.insertBefore(spacer, first);
};

// react-to-pdf cuts one screenshot at fixed heights, so rows that would cross a page edge are moved below it.
// The spacers are removed with the download copy once it unmounts.
const keepRowsOnPages = (target: HTMLElement) => {
  const pageHeight = getPageHeight(target);
  const rootTop = target.getBoundingClientRect().top;

  const walk = (node: Element) => {
    Array.from(node.children).forEach((child) => {
      const { top, bottom, height } = child.getBoundingClientRect();
      const pageEnd = (Math.floor((top - rootTop) / pageHeight) + 1) * pageHeight;
      if (bottom - rootTop <= pageEnd) return;

      if (height <= MAX_ROW_HEIGHT || !child.children.length) {
        // +2px keeps the row clear of the rounding in the page slicing.
        if (height < pageHeight) pushToNextPage(child, pageEnd - (top - rootTop) + 2);
        return;
      }
      walk(child);
    });
  };

  walk(target);
};

const downloadFormStyles = {
  "& .caseFormHeadContainer, & .caseFormCeilContainer": {
    p: 0,
    pb: 2,
    px: 1,
  },
  fontSize: "10px",
};

const useDownloadCaseForm = (filename = "new_document") => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const pdfConfig = useMemo(
    () => ({
      filename: `${filename}.pdf`,
      page: pageSettings,
    }),
    [filename],
  );

  const { toPDF, targetRef } = usePDF(pdfConfig);

  const handleDownloadPaf = useCallback(() => {
    flushSync(() => {
      setIsDownloading(true);
    });
    if (targetRef.current) keepRowsOnPages(targetRef.current);
    toPDF();
    setIsDownloading(false);
  }, [toPDF, targetRef]);

  return { isDownloading, handleDownloadPaf, targetRef, formStyles: isDownloading ? downloadFormStyles : undefined };
};

export default useDownloadCaseForm;
